import { useCallback, useEffect, useState } from 'react';

// specs/001-raben-country-masteradmin/mass-specs/specs/
// 20260826-fe-country-group-folding.md. Shared collapse/expand state for
// country group headers across Structure, both Users tables (Employees +
// Suppliers deliberately share ONE hook instance/storageKey, Goal 4) and
// CompanyFilter's sidebar.
//
// Deliberately dumb about WHAT a "key" is (a country code, or whatever
// "Bez kraju" sentinel the caller already uses -- NO_COUNTRY_VALUE /
// NO_COUNTRY_GROUP_KEY / CompanyFilter's own -- BR-5: it folds like any
// other group). This hook only owns the folding STATE MACHINE: everything
// starts collapsed until the user interacts (Goal 3), persist per
// storageKey after that, and the two bulk actions (Goal 5).
//
// BR-4: this is pure client-side, in-memory + localStorage state. It never
// touches react-query, never appears in a query key, a query param or a
// mutation payload -- see countryFolding.test.tsx's EC-5 test for the spy
// that proves a collapse never refetches anything.

export type RollupStatus = 'none' | 'partial' | 'all';

export interface RollupInfo {
  on: number;
  total: number;
  status: RollupStatus;
}

// BR-2/BR-3/EC-2/EC-7: the collapsed-cell fraction. `total` must always be
// the CALLER-scoped branch count for that group (i.e. `group.branches.length`
// from whatever grouping the screen already narrowed to its own visible
// columns) -- never a separately-fetched "true" global count for the
// country (BR-3).
export function computeRollup(on: number, total: number): RollupInfo {
  let status: RollupStatus;
  if (on <= 0) status = 'none';
  else if (on >= total) status = 'all';
  else status = 'partial';
  return { on, total, status };
}

// Every distinct country key (or `noCountryKey` for the "Bez kraju" bucket,
// BR-5) present in a caller-scoped branch list -- used by each screen to
// compute `allKeys` for expandAll()/collapseAll() below.
export function countryKeysOf(
  branches: Array<{ country_code: string | null }>,
  noCountryKey: string
): string[] {
  const keys = new Set<string>();
  branches.forEach((branch) => {
    keys.add(branch.country_code || noCountryKey);
  });
  return Array.from(keys);
}

export interface UseCountryGroupCollapseArgs {
  // localStorage key this instance persists under. Callers compose it from
  // the current user's id (Open Question 1: "[ASSUMED] localStorage keyed
  // per user id") and a namespace per screen, e.g.
  // `superadmin.countryFold.structure.<userId>` -- so Structure, Users and
  // CompanyFilter each keep their own fold state, while the two Users
  // tables (Employees/Suppliers), which must share one (Goal 4), simply use
  // the SAME key from the same hook call site.
  storageKey: string;
  // Every group key this screen could possibly render, unfiltered by
  // whatever transient country-Select filter is active -- used only by
  // expandAll()/collapseAll() so a bulk action taken while filtered to one
  // country doesn't silently wipe out the recorded state of every OTHER
  // country once the filter is cleared again.
  allKeys: string[];
}

export interface UseCountryGroupCollapseResult {
  isOpen: (key: string) => boolean;
  toggle: (key: string) => void;
  collapseAll: () => void;
  expandAll: () => void;
  // EC-6: true when the user (this visit or persisted from an earlier one)
  // has explicitly collapsed EVERY group under this storageKey, i.e. an
  // actual collapseAll()-equivalent state -- see the doc comment on the
  // return statement below for how callers use this to force a
  // single-rendered group open otherwise.
  isFullyCollapsed: boolean;
}

interface PersistedShape {
  open: string[];
}

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    // Some environments (privacy modes, storage-disabled browsers) throw on
    // ACCESS, not just on read/write -- folding still works in-memory for
    // the current visit.
    return null;
  }
}

function readPersisted(storageKey: string): Set<string> | null {
  const storage = safeLocalStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(storageKey);
    if (raw === null) return null; // never visited -- Goal 3: starts collapsed
    const parsed = JSON.parse(raw) as Partial<PersistedShape> | null;
    if (!parsed || !Array.isArray(parsed.open)) return null;
    return new Set(parsed.open);
  } catch {
    return null;
  }
}

function writePersisted(storageKey: string, open: Set<string>): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    const payload: PersistedShape = { open: Array.from(open) };
    storage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // Quota/availability errors -- degrade to in-memory only, this screen
    // stays fully usable for the current visit.
  }
}

export function useCountryGroupCollapse({
  storageKey,
  allKeys,
}: UseCountryGroupCollapseArgs): UseCountryGroupCollapseResult {
  // Goal 3 (corrected -- see the spec's own note on why "expand my
  // countries" was wrong: scoping is already enforced server-side, so
  // that rule always expanded either a single-country admin's only country
  // or a MasterAdmin's all seventeen, i.e. it always expanded everything).
  // `null` = "no explicit choice recorded yet under this storageKey" -- and
  // with no default-open set to fall back to any more, that means every key
  // is simply closed (see isOpen below) until the user's first interaction
  // materializes a real Set.
  const [openState, setOpenState] = useState<Set<string> | null>(() =>
    readPersisted(storageKey)
  );

  // Re-hydrate if the caller's storageKey identity changes (e.g. the user
  // id resolves after the platform/initial-request load completes, or --
  // in tests -- a different key is used per case).
  useEffect(() => {
    setOpenState(readPersisted(storageKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const isOpen = useCallback(
    (key: string) => (openState ? openState.has(key) : false),
    [openState]
  );

  const persist = useCallback(
    (next: Set<string>) => {
      setOpenState(next);
      writePersisted(storageKey, next);
    },
    [storageKey]
  );

  const toggle = useCallback(
    (key: string) => {
      // Materialize the CURRENT effective state (persisted, or -- on the
      // very first interaction -- "everything closed", Goal 3) before
      // flipping just this key, so untouched sibling groups stay exactly
      // where they were instead of jumping to some other state.
      const base = new Set(openState ?? []);
      if (base.has(key)) {
        base.delete(key);
      } else {
        base.add(key);
      }
      persist(base);
    },
    [openState, persist]
  );

  const collapseAll = useCallback(() => persist(new Set()), [persist]);

  const expandAll = useCallback(
    () => persist(new Set(allKeys)),
    [persist, allKeys]
  );

  return {
    isOpen,
    toggle,
    collapseAll,
    expandAll,
    // EC-6: "[a lone rendered group] is expanded and its header is not
    // collapsible into uselessness; collapse-all still works but leaves the
    // screen empty of detail". A screen narrowed to exactly one group
    // (typically the country-Select filter) forces that sole group open
    // UNLESS this is true -- i.e. unless the CURRENT state really is an
    // explicit "everything collapsed" (collapseAll(), or the last remaining
    // group individually toggled closed), which is the one case the spec
    // calls out as an acceptable, reversible way to still see it collapsed.
    isFullyCollapsed: openState !== null && openState.size === 0,
  };
}
